import type {
  AccountCurrencyTypes,
  AssetClassLevel,
  CustodianStatisticsHolding,
  FinancialProductStrategy,
  Frequency,
  ManualServiceAccountTypes,
  Maybe,
  WorkflowActionTypes,
} from '@generated/graphql';
import type { BankAccount } from '@interfaces/bankAccount';
import type { AssetClassBranch } from '@interfaces/financialProduct';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import type { Institution } from '@interfaces/transfer';
import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioTargetAllocation,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import type { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import type { HouseholdIpsFieldConfig } from './subSteps/householdIpsComparison/householdIpsFields';
import type { FormSignerMapping } from './subSteps/manageSigners/formSignerMapping';

export type CreatedAccountsType = {
  id?: string;
  type?: string;
  state?: string;
};

export const appendUpdatedAccount = (
  context: WorkflowDataType,
  account: CreatedAccountsType,
): WorkflowDataType => {
  if (!account.id) return context;

  const alreadyCreated = context.createdAccounts?.some(
    (createdAccount) => createdAccount.id === account.id,
  );
  if (alreadyCreated) return context;

  const alreadyUpdated = context.updatedAccounts?.some(
    (updatedAccount) => updatedAccount.id === account.id,
  );
  if (alreadyUpdated) return context;

  return {
    ...context,
    updatedAccounts: [
      ...(context.updatedAccounts || []),
      {
        id: account.id,
        type: account.type,
        state: account.state,
      },
    ],
  };
};

/**
 * The Model-tab-only fields of the parent model, i.e. everything in
 * HouseholdModelPortfolioModel except the sections already broken out onto
 * HouseholdModelPortfolioSectionSnapshot itself (children, targetAllocations,
 * assetClassBranches, and the IPS narrative fields). Kept separate so those
 * larger arrays aren't duplicated twice within the same snapshot.
 */
export type HouseholdModelPortfolioModelDetails = Omit<
  HouseholdModelPortfolioModel,
  | 'children'
  | 'assetClassBranches'
  | 'targetAllocations'
  | 'translatedPortfolioDescription'
  | 'translatedInvestmentObjective'
  | 'translatedLiquidity'
  | 'translatedPortfolioDisclaimer'
>;

/**
 * Snapshot of the parent model's sections at the time it was selected, used to detect edits
 * and render the confirmation summary. Reuses the household model portfolio widget's model
 * type since it's already shaped to match FETCH_HOUSEHOLD_MODEL_PORTFOLIO_DETAILS, the same
 * query used to build this snapshot — a generic FinancialProduct/Branch would overstate the
 * fields actually fetched here (e.g. it requires `financialProduct` per branch).
 */
export type HouseholdModelPortfolioSectionSnapshot = {
  details?: HouseholdModelPortfolioModelDetails;
  strategy?: FinancialProductStrategy;
  children?: HouseholdModelPortfolioChild[];
  targetAllocations?: HouseholdModelPortfolioTargetAllocation[];
  assetClassLevel?: AssetClassLevel;
  assetClassBranches?: AssetClassBranch[];
  /** The IPS narrative fields (@see addModelPortfoliosIPSAndAssetClasses). */
  ips?: {
    translatedPortfolioDescription?: TranslatedString;
    translatedInvestmentObjective?: TranslatedString;
    translatedLiquidity?: TranslatedString;
    translatedPortfolioDisclaimer?: TranslatedString;
  };
};

export const reconcileWorkflowAccountContext = (
  context: WorkflowDataType,
): WorkflowDataType => {
  if (!context.createdAccounts) return context;

  const remainingAccounts = context.createdAccounts ?? [];

  if (remainingAccounts.length === 0) {
    return {
      ...context,
      currentAccountId: undefined,
      currentAccountType: undefined,
      currentAccountState: undefined,
    };
  }

  const currentAccount = remainingAccounts.find(
    (account) => account.id === context.currentAccountId,
  );

  if (currentAccount) {
    if (
      currentAccount.type === context.currentAccountType &&
      currentAccount.state === context.currentAccountState
    ) {
      return context;
    }

    return {
      ...context,
      currentAccountType: currentAccount.type,
      currentAccountState: currentAccount.state,
    };
  }

  const lastAccount = remainingAccounts.at(-1)!;

  return {
    ...context,
    currentAccountId: lastAccount.id,
    currentAccountType: lastAccount.type,
    currentAccountState: lastAccount.state,
  };
};

export type TransferContextData = {
  temporaryTransferState?: 'completed' | 'failed';
  substepId?: string;
  temporaryId?: string;
  objectType?: PageObjectType;
  objectId?: string;
  type?: 'EFT' | 'INTERNAL_TRANSFER' | 'EXTERNAL_TRANSFER';
  scheduledTransferId?: string;
  bankAccount?: BankAccount;
  subAccount?: Partial<SubAccount>;
  fromSubAccount?: Partial<SubAccount>;
  institution?: Partial<Institution>;
  accountNumber?: string;
  accountType?: ManualServiceAccountTypes;
  fiscalYear?: number;
  transferType?: string;
  amountCents?: number;
  schedule?: Frequency;
  scheduledDate?: Date | string;
  skipDocuments?: boolean;
  currentTransferId?: string;
  holdings?: CustodianStatisticsHolding;
  participantNumber?: string;
  currency?: string;
  temporaryDeposits?: TransferContextData[];
  currentDepositTemporaryId?: string;
  referenceId?: string; // id of Transfer / ScheduledTransfer / ManualProcess objects that are created once submitted
};

export type WorkflowCustodianDepositTransfer = Partial<{
  confirmationNumber: string;
  accountId: string;
  amount: number;
  bankAccount: Partial<{
    accountNumber: string;
    institutionNumber: string;
    transitNumber: string;
  }>;
  frequency: string;
  fund: Maybe<string>;
  startDate: string | Date;
}>;

/**
 * Workflow handshake:
 *  1. DepositReview writes `input` to context and advances to the next step.
 *  2. SynchronousIntegration triggers a Windmill script that reads `input`,
 *     calls the custodian PAC API, and writes `response` back to context.
 *     action: complete on SUCCESS, redirect on CHALLENGE, or cancel on FAILURE.
 *
 * These types are not available via GraphQL codegen. They are a frontend-first
 * typed contract owned by us (FE + Windmill). The backend workflow context is
 * untyped (`any`). A follow-up ticket will formalize these in a
 * shared library across FE, BE, and Windmill.
 *
 * @see https://onevest.getoutline.com/doc/estimation-breakdown-6PmlFQrY9E
 */

/** Data needed by the Windmill script to call the custodian PAC API. */
export type CustodianDepositTransferContextInput = {
  /** session ID (from sessionStorage) for custodian risk engine. */
  cmid?: string;
  accountId?: string;
  organizationUserId?: string;
  amount?: number;
  /** CUSIP of the target financial product. */
  fund?: string;
  frequency?: string;
  startDate?: string;
  bankAccount?: {
    accountNumber?: string;
    institutionNumber?: string;
    transitNumber?: string;
  };
};

/** Response written by the Windmill script after calling the custodian PAC API. */
export type CustodianDepositTransferContextResponse = {
  status?: 'SUCCESS' | 'FAILURE' | 'CHALLENGE';
  transactionId?: string;
  confirmationNumber?: string;
  /** Present when the custodian risk engine requires additional verification. */
  action?: {
    type: string;
    details?: Array<{ key: string; value: unknown }>;
  };
  error?: {
    message: string;
    code: string;
  };
};

/**
 * Persisted on `WorkflowCompletion context custodianDepositTransfer`.
 * `input` is written by DepositReview; `response` is written by the Windmill script.
 * `challengeResult` is written by the RedirectHandler after the user returns from external MFA.
 */
export type CustodianDepositTransferContext = {
  input?: CustodianDepositTransferContextInput;
  response?: CustodianDepositTransferContextResponse;
  challengeResult?: {
    /**
     * SUCCESS/FAILURE are written on return from the external MFA page.
     * RETRYING is a transient marker set during handleRetry and overwritten
     * on the next MFA return — it is never the final persisted state.
     */
    status: 'SUCCESS' | 'FAILURE' | 'RETRYING';
    transactionId?: string;
    retryCount?: number;
    processedAt: string;
  };
};

export type WorkflowDataType = TransferContextData & {
  // Users
  currentUserId?: string;

  // Accounts
  currentAccountId?: string;
  currentAccountType?: string;
  currentAccountState?: string;
  createdAccounts?: CreatedAccountsType[];
  updatedAccounts?: CreatedAccountsType[];
  skipEditAccount?: boolean;

  // Goals
  currentGoalId?: string;
  currentGoalType?: string;
  currentGoalName?: string;
  createdGoalIds?: string[];
  goalTimeHorizons?: any;
  goalRiskQuestions?: any;
  goalRiskQuestionTitle?: TranslatedString;

  // Sub-Accounts
  currentSubAccountId?: string;
  createdSubAccountIds?: string[];

  // Households
  currentHouseholdClientGroupId?: string;
  /** Owning org of the bound entity; sent to BE as organizationId. */
  entityOrganizationId?: string;
  linkingHouseholdId?: string;
  isHouseholdRemoved?: boolean;

  // Transfers - Deposit or Withdrawal
  createdTransfers?: {
    id: string;
  }[];
  createdScheduledTransfers?: {
    id: string;
  }[];
  createdExternalTransfers?: {
    id: string;
    subAccount: {
      id: string;
      account: { id: string };
    };
  }[];
  createdCustodianDepositTransfers?: WorkflowCustodianDepositTransfer[];
  custodianDepositTransfer?: CustodianDepositTransferContext;

  // Model Portfolios
  modelPortfolioDetails?: any;

  // Household Model Portfolio Initialize (mint-late)
  // Household id: currentHouseholdClientGroupId (completion objectId)
  baseModelPortfolioId?: string;
  tradingProcess?: string;
  baseModelSnapshot?: HouseholdModelPortfolioSectionSnapshot;
  /** Populated after setupEntityBoundModel succeeds for attach-only retry. */
  entityBoundModelPortfolioId?: string;
  /**
   * The name the advisor gave the household's copy on Initialize, written
   * beside the clone id once it has been sent, so a workflow picked up again
   * shows the name the copy went out with rather than an empty box. Absent
   * when they left the naming to product-service, which is the usual case.
   */
  householdModelPortfolioName?: TranslatedString;
  /**
   * The household's model as it stood before the workflow touched it, so the
   * IPS steps can show what is there today beside what is being proposed.
   * Written once, when the draft is seeded, because Initialize attaches the new
   * clone to the household long before those steps run — by then the household
   * no longer remembers what it had.
   */
  householdModelPortfolioBaseline?: HouseholdModelPortfolioModel;
  /**
   * The IPS fields the editing step was configured with, left behind for the
   * steps that only recap them. Those steps follow this rather than carrying
   * their own copy of the same settings, which an admin could configure to
   * disagree with what the advisor was actually allowed to fill in.
   */
  householdIpsFieldConfig?: HouseholdIpsFieldConfig;
  /**
   * The proposal the allocation steps are working against.
   *
   * Mirrors the field workflow-service keeps on the same context: its
   * transition action reads this to know which proposal an approval decision
   * belongs to, so a step that drafts one has to leave the id here. It is also
   * how a workflow resumed part way through finds the proposal it already
   * opened, which product-service would otherwise refuse to let it open twice.
   */
  modelChangeProposalId?: string;

  // Section Navigation
  segmentHistory?: string[];
  currentSegment?: string | null;

  // Other
  childWorkflowCompletion?: any;

  // Signing
  signerMapping?: FormSignerMapping[];
  integrationFormPackId?: string;
  formMapping?: {
    formDefinitionKey: string;
    objectId: string;
    objectType: string;
  }[];

  userAllowedNumberOfAttempts?: any;
  usedVerificationAttempts?: any;
  isUserVerified?: any;

  // Net Worth
  netWorthData?: {
    externalSourceType?: string;
    netWorthAccountNickname?: Partial<TranslatedString>;
    netWorthAmountCents?: number;
    publicToken?: string;
    netWorthLinking?: 'MANUAL' | 'AUTOMATIC';
    netWorthStep?: number;
    netWorthLinkingActionType?: WorkflowActionTypes;
    netWorthLinkingNotificationGroup?: string;
    netWorthCurrency?: AccountCurrencyTypes;
    notificationSuccessTitle?: string;
    notificationSuccessBody?: string;
    notificationFailureTitle?: string;
    notificationFailureBody?: string;
    notificationLinkText?: string;
    notificationLink?: string;
  };
  body?: Record<string, unknown>;
  integrationResult?: {
    status: 'success' | 'error';
    data?: unknown;
    error?: { code?: string; message?: string };
  };
};

export const toTransferContextData = (
  workflowContext: WorkflowDataType,
): TransferContextData => ({
  substepId: workflowContext.substepId,
  temporaryId: workflowContext.temporaryId,
  objectType: workflowContext.objectType,
  objectId: workflowContext.objectId,
  type: workflowContext.type,
  bankAccount: workflowContext.bankAccount,
  subAccount: workflowContext.subAccount,
  fromSubAccount: workflowContext.fromSubAccount,
  institution: workflowContext.institution,
  accountNumber: workflowContext.accountNumber,
  accountType: workflowContext.accountType,
  fiscalYear: workflowContext.fiscalYear,
  transferType: workflowContext.transferType,
  amountCents: workflowContext.amountCents,
  schedule: workflowContext.schedule,
  scheduledDate: workflowContext.scheduledDate,
  skipDocuments: workflowContext.skipDocuments,
  currentTransferId: workflowContext.currentTransferId,
  holdings: workflowContext.holdings,
  participantNumber: workflowContext.participantNumber,
});

export const CREATED_OBJECT_FIELDS = [
  'createdAccounts',
  'createdGoalIds',
  'createdSubAccountIds',
  'createdTransfers',
  'createdScheduledTransfers',
  'createdExternalTransfers',
  'createdCustodianDepositTransfers',
] as const satisfies ReadonlyArray<keyof WorkflowDataType>;

export const buildClearedCreatedFields = (): Pick<
  WorkflowDataType,
  (typeof CREATED_OBJECT_FIELDS)[number]
> =>
  Object.fromEntries(CREATED_OBJECT_FIELDS.map((key) => [key, []])) as Pick<
    WorkflowDataType,
    (typeof CREATED_OBJECT_FIELDS)[number]
  >;
